import React, { useEffect, useState, useCallback } from 'react';
import WorkOrders from '../../models/WorkOrders';
import DataTable from 'react-data-table-component';
import { ImSpinner3 } from 'react-icons/im';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import moment from 'moment';
import pLimit from 'p-limit';

const InspectionWorkOrdersSection = ({
  viewMode,
  startDate,
  endDate,
  user,
  currentUserData,
  selectedPropertyList,
  isExpanded,
  setIsExpanded,
  workOrders: propWorkOrders,
  filteredWorkOrders: propFilteredWorkOrders,
  loading: propLoading
}) => {
  // Use props from parent if available, otherwise manage own state (backward compatibility)
  const [workOrders, setWorkOrders] = useState([]);
  const [filteredWorkOrders, setFilteredWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use parent state if available
  const effectiveWorkOrders = propWorkOrders !== undefined ? propWorkOrders : workOrders;
  const effectiveFilteredWorkOrders = propFilteredWorkOrders !== undefined ? propFilteredWorkOrders : filteredWorkOrders;
  const effectiveLoading = propLoading !== undefined ? propLoading : loading;

  const CHUNK_SIZE = 100;
  const MAX_CONCURRENCY = 6;
  const limit = pLimit(MAX_CONCURRENCY);

  // Fetch chunk of work orders by IDs
  const fetchChunk = async (ids) => {
    const searchList = [{ field: 'work_order_id', value: ids, comparison: 'IN' }];
    const { data } = await WorkOrders.filterBy(
      searchList,
      'dt_created',
      'desc',
      10000,
      undefined,
      undefined,
      undefined,
      true
    );
    return data;
  };

  // Fetch all work orders in date range
  const fetchWorkOrdersByDateRange = useCallback(async () => {
    console.log('[InspectionWorkOrdersSection] Fetching work orders by date range');
    setLoading(true);
    setError(null);

    try {
      // Get work order IDs by date range
      const workOrderIds = await WorkOrders.getWorkOrderIdsByDateRange(
        moment(startDate).format('YYYY-MM-DD HH:mm:ss'),
        moment(endDate).add(1, 'day').format('YYYY-MM-DD HH:mm:ss')
      );

      console.log('[InspectionWorkOrdersSection] Work Order IDs by Date Range:', workOrderIds.length);

      // Break work order IDs into chunks
      const chunks = [];
      for (let i = 0; i < workOrderIds.length; i += CHUNK_SIZE) {
        chunks.push(workOrderIds.slice(i, i + CHUNK_SIZE));
      }

      // Fetch work orders for each chunk in parallel
      let allWorkOrders = [];
      await Promise.all(
        chunks.map((chunk) =>
          limit(() => fetchChunk(chunk).then((res) => allWorkOrders.push(...res)))
        )
      );

      console.log('[InspectionWorkOrdersSection] Total work orders fetched:', allWorkOrders.length);
      setWorkOrders(allWorkOrders);
    } catch (err) {
      console.error('[InspectionWorkOrdersSection] Error fetching work orders:', err);
      setError(err);
      setWorkOrders([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Apply filtering based on view mode
  const applyViewModeFiltering = useCallback(() => {
    console.log('[InspectionWorkOrdersSection] Applying filtering for viewMode:', viewMode);

    if (viewMode === 'favorited') {
      // Filter by favorited properties
      let favoriteUuids = [];
      if (currentUserData?.favorite_properties) {
        try {
          const parsedFavorites = JSON.parse(currentUserData.favorite_properties);
          favoriteUuids = Array.isArray(parsedFavorites) ? parsedFavorites : [];
        } catch (parseError) {
          console.warn('[InspectionWorkOrdersSection] Error parsing favorite_properties:', parseError);
          favoriteUuids = [];
        }
      }

      if (favoriteUuids.length === 0) {
        setFilteredWorkOrders([]);
        return;
      }

      const favoritedWorkOrders = workOrders.filter(
        (workOrder) => workOrder.property_uuid && favoriteUuids.includes(workOrder.property_uuid)
      );
      console.log('[InspectionWorkOrdersSection] Favorited work orders:', favoritedWorkOrders.length);
      setFilteredWorkOrders(favoritedWorkOrders);
    } else if (viewMode === 'propertyList') {
      // Filter by selected property list
      if (!selectedPropertyList) {
        setFilteredWorkOrders([]);
        return;
      }

      let propertyUuids = [];
      if (selectedPropertyList.properties_list) {
        try {
          propertyUuids = JSON.parse(selectedPropertyList.properties_list);
        } catch (e) {
          console.error('[InspectionWorkOrdersSection] Error parsing properties_list:', e);
          propertyUuids = [];
        }
      }

      if (propertyUuids.length === 0) {
        setFilteredWorkOrders([]);
        return;
      }

      const propertyListWorkOrders = workOrders.filter(
        (workOrder) => workOrder.property_uuid && propertyUuids.includes(workOrder.property_uuid)
      );
      console.log('[InspectionWorkOrdersSection] Property list work orders:', propertyListWorkOrders.length);
      setFilteredWorkOrders(propertyListWorkOrders);
    } else {
      // Show all work orders (inspection view)
      console.log('[InspectionWorkOrdersSection] Showing all work orders:', workOrders.length);
      setFilteredWorkOrders(workOrders);
    }
  }, [viewMode, workOrders, currentUserData, selectedPropertyList]);

  // Fetch work orders when date range changes (only if not using parent state)
  useEffect(() => {
    if (propWorkOrders === undefined && isExpanded && startDate && endDate) {
      fetchWorkOrdersByDateRange();
    }
  }, [startDate, endDate, isExpanded, fetchWorkOrdersByDateRange, propWorkOrders]);

  // Apply filtering when view mode or work orders change (only if not using parent state)
  useEffect(() => {
    if (propFilteredWorkOrders === undefined) {
      applyViewModeFiltering();
    }
  }, [applyViewModeFiltering, propFilteredWorkOrders]);

  // Helper function to safely parse dates for sorting
  const sortDate = (rowA, rowB, selector) => {
    const dateA = rowA[selector] ? new Date(rowA[selector]) : null;
    const dateB = rowB[selector] ? new Date(rowB[selector]) : null;

    if (!dateA && !dateB) return 0;
    if (!dateA) return -1;
    if (!dateB) return 1;

    return dateA.getTime() - dateB.getTime();
  };

  // Column definitions
  const columns = [
    {
      name: 'Work Order Number',
      selector: (row) => row.number,
      sortable: true,
      width: '120px',
      wrap: true,
      id: 'number',
    },
    {
      name: 'Work Order ID',
      selector: (row) => row.work_order_id,
      sortable: true,
      width: '140px',
      wrap: true,
      id: 'work_order_id',
    },
    {
      name: 'Property Number',
      selector: (row) => row.property_number,
      sortable: true,
      width: '140px',
      wrap: true,
      id: 'property_number',
    },
    {
      name: 'Root Component',
      selector: (row) => row.root_comp,
      sortable: true,
      width: '200px',
      id: 'root_comp',
      cell: (row) => (
        <div
          title={row.root_comp}
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.root_comp || 'N/A'}
        </div>
      ),
    },
    {
      name: 'Created At',
      selector: (row) => row.dt_created,
      sortable: true,
      width: '170px',
      wrap: true,
      sortFunction: (rowA, rowB) => sortDate(rowA, rowB, 'dt_created'),
      id: 'dt_created',
    },
    {
      name: 'Status',
      selector: (row) => row.status_id,
      sortable: true,
      width: '100px',
      wrap: true,
      id: 'status_id',
    },
    {
      name: 'Task Refinement',
      selector: (row) => row.task_refinement,
      sortable: true,
      width: '300px',
      id: 'task_refinement',
      cell: (row) => (
        <div
          title={row.task_refinement}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2',
            maxHeight: '2.4em',
          }}
        >
          {row.task_refinement || 'N/A'}
        </div>
      ),
    },
    {
      name: 'Notes',
      selector: (row) => row.note_text,
      sortable: true,
      width: '250px',
      id: 'note_text',
      cell: (row) => (
        <div
          title={row.note_text}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2',
            maxHeight: '2.4em',
          }}
        >
          {row.note_text || 'N/A'}
        </div>
      ),
    },
    {
      name: 'Completion Notes',
      selector: (row) => row.completion_note_text,
      sortable: true,
      width: '250px',
      id: 'completion_note_text',
      cell: (row) => (
        <div
          title={row.completion_note_text}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2',
            maxHeight: '2.4em',
          }}
        >
          {row.completion_note_text || 'N/A'}
        </div>
      ),
    },
    {
      name: 'Compressor Failure',
      selector: (row) => row.compressor_failure_detected,
      sortable: true,
      width: '140px',
      wrap: true,
      id: 'compressor_failure_detected',
      cell: (row) => {
        if (row.compressor_failure_detected === null || row.compressor_failure_detected === undefined) {
          return 'N/A';
        }
        return row.compressor_failure_detected ? 'Yes' : 'No';
      },
    },
    {
      name: 'Confidence',
      selector: (row) => row.compressor_failure_confidence,
      sortable: true,
      width: '120px',
      wrap: true,
      id: 'compressor_failure_confidence',
      cell: (row) => row.compressor_failure_confidence || 'N/A',
    },
    {
      name: 'Reasoning',
      selector: (row) => row.compressor_failure_reasoning,
      sortable: true,
      width: '350px',
      id: 'compressor_failure_reasoning',
      cell: (row) => (
        <div
          title={row.compressor_failure_reasoning}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2',
            maxHeight: '2.4em',
          }}
        >
          {row.compressor_failure_reasoning || 'N/A'}
        </div>
      ),
    },
  ];

  // Get view-specific empty message
  const getEmptyMessage = () => {
    if (viewMode === 'favorited') {
      return 'No work orders found for your favorited properties in this date range.';
    } else if (viewMode === 'propertyList' && selectedPropertyList) {
      return `No work orders found for properties in "${selectedPropertyList.name}" in this date range.`;
    } else {
      return 'No work orders found in this date range.';
    }
  };

  return (
    <div style={{ marginBottom: '20px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
      {/* Collapsible Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          cursor: 'pointer',
          backgroundColor: '#f8f9fa',
          borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
          borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>
            Work Orders
          </h3>
          {!effectiveLoading && (
            <span
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              {effectiveFilteredWorkOrders.length}
            </span>
          )}
        </div>
        <div style={{ fontSize: '20px', color: '#666' }}>
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </div>
      </div>

      {/* Table Content */}
      {isExpanded && (
        <div style={{ padding: '20px' }}>
          {effectiveLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <ImSpinner3 className="icon-spin" style={{ fontSize: 40 }} />
              <p style={{ marginTop: '16px', color: '#666' }}>Loading work orders...</p>
            </div>
          ) : error ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#dc3545' }}>
              <p style={{ fontSize: '16px' }}>Error loading work orders</p>
              <p style={{ fontSize: '14px', color: '#666' }}>{error.message}</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={effectiveFilteredWorkOrders}
              noDataComponent={
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  {getEmptyMessage()}
                </div>
              }
              responsive
              highlightOnHover
              persistTableHead
              defaultSortFieldId="dt_created"
              defaultSortAsc={false}
              pagination
              paginationPerPage={100}
              paginationRowsPerPageOptions={[100, 250, 500]}
              paginationComponentOptions={{
                rowsPerPageText: 'Rows per page:',
                rangeSeparatorText: 'of',
                noRowsPerPage: false,
                selectAllRowsItem: false,
              }}
              style={{ border: '1px solid #ddd', borderRadius: '8px' }}
              customStyles={{
                headCells: {
                  style: {
                    backgroundColor: '#e3e3e3',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    color: '#333',
                  },
                },
                cells: {
                  style: {
                    padding: '6px 8px',
                    fontSize: '12px',
                    color: '#333',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxHeight: '40px',
                    lineHeight: '1.4',
                  },
                },
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default InspectionWorkOrdersSection;
